import React, { useCallback, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import {
  Modal,
  Button,
  TextInput,
  SelectDropdown,
  RadioButton,
  Checkbox,
  DatePicker,
  SegmentControl,
  Tooltip,
} from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import _ from "lodash";

import { END_TYPES } from "../../context/RepeatEventContext";
import { useRepeatEventLogic } from "../../hooks/RepeatEvent/useRepeatEventLogic";
import classes from "./RepeatEventModal.scss";
import { CaretDownFilled, WarningTriangleOutlined } from "@toddle-edu/ds-icons";
import { getDateMomentObject } from "Utils/DateTimeHelpers";
import {
  getTabs,
  getCurrentDateObject,
  getSummaryText,
  DAYS,
  getErrorText,
} from "MyCalendar/modules/Selector";
import {
  calculateOccurrencesByFrequency,
  DEFAULT_OCCURRENCES,
  getWeekOfMonth,
} from "../../modules/Utils";

const WeeklyRepeatDays = ({
  t,
  handleWeekDayClick,
  weekDays,
  days,
  eventStartDate,
  operationalDays,
  skipNonOperationalDays,
}) => {
  const { day } = getCurrentDateObject(eventStartDate);
  useEffect(() => {
    if (!_.isEmpty(weekDays)) return;
    handleWeekDayClick(
      _.map(
        _.filter(days, weekday => _.get(weekday, "value") === day),
        "value"
      )
    );
  }, []);

  return (
    <div className={classes.everySection}>
      <span className={classes.everyLabel}>{t("calendar:on")}</span>
      <div className={classes.daysContainer}>
        {_.map(days, day => {
          const isSelected = _.includes(weekDays, _.get(day, "value"));

          const handleClick = () => {
            if (isSelected) {
              handleWeekDayClick(
                _.filter(weekDays, weekday => weekday !== day.value)
              );
              return;
            }
            handleWeekDayClick([...weekDays, _.get(day, "value")]);
          };

          const dayLabelClass = classNames(classes.dayLabel, "text-body", {
            [classes.daySelected]: isSelected,
          });

          return (
            <div
              className={dayLabelClass}
              onClick={handleClick}
              key={day.value}
            >
              {_.get(day, "label")}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MonthlyRepeatDays = ({
  t,
  eventStartDate,
  handleMonthClick,
  handlePositions,
  handleWeekDayClick,
  monthDays,
  setPositions,
  setSkipNonOperationalDays,
}) => {
  const tabs = getTabs({ t, eventStartDate });
  const [selectedTab, setSelectedTab] = useState("DAY");
  const { day, dayNumber, weekOfMonth } = getCurrentDateObject(eventStartDate);

  useEffect(() => {
    if (_.isEmpty(monthDays) && _.isEmpty(setPositions)) {
      handleMonthClick([dayNumber]);
    }
  }, []);

  useEffect(() => {
    if (_.isEmpty(monthDays)) {
      setSelectedTab("WEEK");
    } else {
      setSelectedTab("DAY");
    }
  }, [monthDays]);

  const handleChange = value => {
    setSelectedTab(value);
    setSkipNonOperationalDays(false);
    if (value === "DAY") {
      handleMonthClick([dayNumber]);
    } else {
      handlePositions([getWeekOfMonth(weekOfMonth)]); // -1 indicates last occurrence of the weekday in the month
      handleWeekDayClick([day]);
    }
  };

  return (
    <div className={classes.everySection}>
      <span className={classes.everyLabel}>{t("calendar:on")}</span>
      <div className={classes.daysContainer}>
        <SegmentControl
          dsVersion="2.0"
          options={tabs}
          value={selectedTab}
          onChange={handleChange}
          variant="text"
          size="medium"
          type="flexible"
        />
      </div>
    </div>
  );
};

const RepeatEventModalContent = ({
  isOpen = true,
  onClose,
  onDone,
  testId,
  academicYearEndDate,
  eventStartDate,
  skipNonOperationalDays,
  setSkipNonOperationalDays,
  operationalDays,
  repeatTitleLocaleKey = "calendar:repeat",
}) => {
  const { t } = useI18n(["calendar", "common"]);
  const {
    state,
    actions,
    repeatTypeOptions,
    isValidConfiguration,
    handleIntervalChange,
    handleOccurrencesChange,
    REPEAT_TYPES,
  } = useRepeatEventLogic();

  const {
    interval,
    repeatType,
    endType,
    occurrences,
    weekDays,
    monthDays,
    setPositions,
    endDate,
  } = state;

  const actionRef = useRef(actions);
  actionRef.current = actions;

  const totalRemainingOccurrences = calculateOccurrencesByFrequency(
    { ...state, frequency: repeatType },
    academicYearEndDate,
    eventStartDate
  );

  const [onEndDate, setOnEndDate] = useState(
    getDateMomentObject(endDate).format()
  );

  const handleMonthClick = useCallback(
    monthDays => {
      actionRef.current.setMonthDays(monthDays);
    },
    [actionRef]
  );

  const handleDone = useCallback(() => {
    if (isValidConfiguration && onDone) {
      onDone(state);
    }
  }, [isValidConfiguration, onDone, state]);

  const handleCancel = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const handleRepeatTypeChange = useCallback(
    selectedOption => {
      // reset weekDays if switching to weekly
      if (
        selectedOption.value === "WEEKLY" &&
        selectedRepeatTypeOption?.value !== "WEEKLY"
      ) {
        actions.setWeekDays([getCurrentDateObject(eventStartDate)?.day]);
        setSkipNonOperationalDays(false);
      }
      if (selectedOption.value === "DAILY") {
        actions.setDays();
      }
      actions.setRepeatType(selectedOption.value);
    },
    [actions, eventStartDate]
  );

  const handleWeekDayClick = useCallback(
    weekDays => {
      actions.setWeekDays(weekDays);
    },
    [actions]
  );

  const handlePositions = useCallback(
    positions => {
      actions.setPositions(positions);
    },
    [actions]
  );

  const handleEndTypeChange = useCallback(
    value => {
      actions.setEndType(value);
      if (value === END_TYPES.ACADEMIC_YEAR) {
        actions.setEndDate(academicYearEndDate);
      }
      if (value === END_TYPES.ON_DATE) {
        actions.setEndDate(onEndDate);
      }
      if (value === END_TYPES.AFTER_OCCURRENCES) {
        actions.setOccurrences(occurrences || DEFAULT_OCCURRENCES);
      }
    },
    [actions, academicYearEndDate, onEndDate, occurrences]
  );

  const maxOccouranceError =
    occurrences > totalRemainingOccurrences &&
    endType === END_TYPES.AFTER_OCCURRENCES;

  // Common validation function for numeric inputs
  const validateNumericInput = useCallback((event, onChangeHandler) => {
    const value = event.target.value;
    if (value?.includes("-") || value?.includes("+")) {
      event.target.value = value.replace(/[-+]/g, "");
    }
    if (value === "0") {
      event.target.value = "1";
    }
    onChangeHandler(event.target.value);
  }, []);

  // Wrapper for interval change to handle TextInput event
  const handleIntervalInputChange = useCallback(
    event => {
      validateNumericInput(event, handleIntervalChange);
    },
    [validateNumericInput, handleIntervalChange]
  );

  // Wrapper for occurrences change to handle TextInput event
  const handleOccurrencesInputChange = useCallback(
    event => {
      validateNumericInput(event, handleOccurrencesChange);
    },
    [validateNumericInput, handleOccurrencesChange]
  );

  const selectedRepeatTypeOption = repeatTypeOptions.find(
    option => option.value === repeatType
  );

  const getMaxAllowedDate = useCallback(() => {
    const academicEnd = getDateMomentObject(academicYearEndDate);
    // return academicEnd.isAfter(eventStart) ? eventStart : academicEnd;
    return academicEnd;
  }, [academicYearEndDate]);

  useEffect(() => {
    if (isOpen) {
      // when modal opens, set the onEndDate based on endType
      const isOnDate = endType === END_TYPES.ON_DATE;
      const maxDate = isOnDate
        ? getDateMomentObject(endDate)
        : getMaxAllowedDate();
      setOnEndDate(maxDate.format());
    }
  }, [isOpen]);

  const handleEndDateChange = date => {
    actions.setEndDate(date.format());
    setOnEndDate(date.format());
  };

  const skipNonOperationalDaysHandler = () => {
    setSkipNonOperationalDays(!skipNonOperationalDays);
  };

  const { subtext: summaryText } = getSummaryText({
    t,
    state,
    date: eventStartDate,
  });

  const { errorText, disableDoneButton } = getErrorText({
    t,
    state,
    operationalDays,
    skipNonOperationalDays,
  });

  const endTypeHandler = type => () => handleEndTypeChange(type);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      className={classes.modal}
      testId={`${testId}-modal`}
      height="fit-content"
      width="480px"
    >
      <div className={classes.content}>
        <h2 className={classes.title}>{t(repeatTitleLocaleKey)}</h2>

        {/* Every section */}
        <div className={classes.everySection}>
          <span className={classes.everyLabel}>{t("calendar:every")}</span>

          <div className={classes.intervalContainer}>
            <TextInput
              defaultValue={interval.toString()}
              onChange={handleIntervalInputChange}
              className={classes.intervalInput}
              type="number"
              min="1"
              dsVersion="2.0"
              testId={`${testId}-interval`}
            />
          </div>

          <SelectDropdown
            name="repeat-type"
            placeholder={t("calendar:select_the_event_type")}
            options={repeatTypeOptions}
            value={selectedRepeatTypeOption}
            onChange={handleRepeatTypeChange}
            className={classes.repeatTypeDropdown}
            isMulti={false}
            isClearable={false}
            size="medium"
            variant="outlined"
            testId={`${testId}-repeat-type`}
          />
        </div>

        {selectedRepeatTypeOption?.value === "WEEKLY" ? (
          <WeeklyRepeatDays
            t={t}
            eventStartDate={eventStartDate}
            handleWeekDayClick={handleWeekDayClick}
            weekDays={weekDays}
            days={DAYS}
            operationalDays={operationalDays}
            skipNonOperationalDays={skipNonOperationalDays}
          />
        ) : null}

        {selectedRepeatTypeOption?.value === "MONTHLY" ? (
          <MonthlyRepeatDays
            t={t}
            eventStartDate={eventStartDate}
            handleMonthClick={handleMonthClick}
            handlePositions={handlePositions}
            handleWeekDayClick={handleWeekDayClick}
            monthDays={monthDays}
            setPositions={setPositions}
            setSkipNonOperationalDays={setSkipNonOperationalDays}
          />
        ) : null}

        {/* Skip non-operational days */}
        {_.isEmpty(weekDays) &&
        selectedRepeatTypeOption?.value !== REPEAT_TYPES.WEEK ? (
          <div className={classes.checkboxSection}>
            <Checkbox
              isChecked={skipNonOperationalDays}
              onChange={skipNonOperationalDaysHandler}
              testId={`${testId}-skip-non-operational-days`}
            >
              <div className={classNames("text-body", classes.label)}>
                {t("calendar:skip_non_operational_days")}
              </div>
            </Checkbox>
          </div>
        ) : null}

        {/* And ends section */}
        <div className={classes.endsSection}>
          <h3 className={classes.endsTitle}>{t("calendar:and_ends")}</h3>

          <div className={classes.endOptions}>
            {/* Until end of academic year */}
            <div className={classes.endOption}>
              <RadioButton
                checked={endType === END_TYPES.ACADEMIC_YEAR}
                onChange={endTypeHandler(END_TYPES.ACADEMIC_YEAR)}
                label={t("calendar:until_end_of_current_academic_year")}
                testId={`${testId}-end-academic-year`}
              >
                <div className={classNames("text-body", classes.label)}>
                  {t("calendar:until_end_of_current_academic_year")}
                </div>
              </RadioButton>
            </div>

            {/* On specific date */}
            <div className={classes.endOption}>
              <div className={classes.endOptionWithInput}>
                <RadioButton
                  checked={endType === END_TYPES.ON_DATE}
                  onChange={endTypeHandler(END_TYPES.ON_DATE)}
                  label={t("calendar:on")}
                  testId={`${testId}-end-on-date`}
                >
                  <div className={classNames("text-body", classes.onLabel)}>
                    {t("calendar:on")}
                  </div>
                </RadioButton>

                <DatePicker
                  value={getDateMomentObject(onEndDate)}
                  onChange={handleEndDateChange}
                  disabled={endType !== END_TYPES.ON_DATE}
                  minDate={getDateMomentObject(eventStartDate)}
                  maxDate={getDateMomentObject(academicYearEndDate)}
                  key={`date-picker-${onEndDate}`}
                >
                  <TextInput
                    value={getDateMomentObject(onEndDate).format(
                      "ddd MMM D, YYYY"
                    )}
                    disabled={endType !== END_TYPES.ON_DATE}
                    trailingIcon={<CaretDownFilled variant="subtle" />}
                    readOnly={true}
                    testId={`${testId}-end-date-picker`}
                  />
                </DatePicker>
              </div>
            </div>

            {/* After occurrences */}
            <div className={classes.endOption}>
              <div className={classes.endOptionWithInput}>
                <RadioButton
                  checked={endType === END_TYPES.AFTER_OCCURRENCES}
                  onChange={endTypeHandler(END_TYPES.AFTER_OCCURRENCES)}
                  label={t("calendar:after")}
                  testId={`${testId}-end-after-occurrences`}
                >
                  <div className={classNames("text-body", classes.label)}>
                    {t("calendar:after")}
                  </div>
                </RadioButton>
                <div className={classes.occurrencesContainer}>
                  <TextInput
                    defaultValue={occurrences}
                    max={`${totalRemainingOccurrences * 100}`} // setting a high max value to adjust the input field width
                    onInput={handleOccurrencesInputChange}
                    disabled={endType !== END_TYPES.AFTER_OCCURRENCES}
                    className={classNames(classes.occurrencesInput, {
                      [classes.disabled]:
                        endType !== END_TYPES.AFTER_OCCURRENCES,
                    })}
                    type="number"
                    min="1"
                    dsVersion="2.0"
                    testId={`${testId}-occurrences`}
                  />
                  <span className={classes.occurrencesLabel}>
                    {occurrences > 1
                      ? t("calendar:occurrences")
                      : t("calendar:occurrence")}
                  </span>
                </div>
              </div>
              {maxOccouranceError ? (
                <div className={classes.warning}>
                  <WarningTriangleOutlined
                    size={"xxx-small"}
                    variant={"warning"}
                  />
                  <span
                    className={classNames(classes.warningText, "text-body-s")}
                  >
                    {t("calendar:max_occurrences_reached", {
                      date: getDateMomentObject(academicYearEndDate).format(
                        "Do [of] MMM, YYYY"
                      ),
                    })}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className={classes.divider} />

        {/* Summary */}
        <div className={classes.summary}>
          <span className={classes.summaryText}>{summaryText}</span>
        </div>
        {/* Warning message */}
        {errorText ? (
          <div className={classes.warning}>
            <WarningTriangleOutlined size={"xxx-small"} variant={"warning"} />
            <span className={classNames(classes.warningText, "text-body-s")}>
              {errorText}
            </span>
          </div>
        ) : null}
      </div>

      {/* Footer */}
      <div className={classes.footer}>
        <div className={classes.footerButtons}>
          <Button
            variant="neutral"
            type="outlined"
            onClick={handleCancel}
            testId={`${testId}-cancel`}
          >
            {t("common:cancel")}
          </Button>
          {!isValidConfiguration || disableDoneButton ? (
            <Tooltip
              tooltip={t("calendar:scheduled_err_msg")}
              placement="leftBottom"
              showArrow={true}
            >
              <Button
                variant="progressive"
                type="fill"
                onClick={handleDone}
                disabled={!isValidConfiguration || disableDoneButton}
                testId={`${testId}-done`}
              >
                {t("common:done")}
              </Button>
            </Tooltip>
          ) : (
            <Button
              variant="progressive"
              type="fill"
              onClick={handleDone}
              testId={`${testId}-done`}
            >
              {t("common:done")}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

RepeatEventModalContent.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  testId: PropTypes.string,
};

RepeatEventModalContent.defaultProps = {
  isOpen: true,
  testId: "repeat-event-modal",
};

const RepeatEventModal = ({ academicYearEndDate, ...props }) => (
  <RepeatEventModalContent
    {...props}
    academicYearEndDate={academicYearEndDate}
  />
);

RepeatEventModal.propTypes = {
  academicYearEndDate: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  testId: PropTypes.string,
};

RepeatEventModal.defaultProps = {
  isOpen: true,
  testId: "repeat-event-modal",
};

export default RepeatEventModal;
